import React from "react";

import AddProductModal from "./Products/components/AddProductModal";
import ProductOnsiteSheet from "./Products/components/OnSiteSheet";
import ProductDetailSheet from "./Products/components/ProductDetailSheet";
import ProductLog from "./Products/components/ProductLog";
import UpdateProductModal from "./Products/components/UpdateProductModal";
import AdjustQuantityModal from "./Products/product-detail-page/components/AdjustQuantityModal";
import ConsumedTable from "./Products/table/ConsumedTable";
import ProductTable from "./Products/table/ProductTable";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import { baseFilterParams } from "@/constants/constants";
import { productTabs } from "@/constants/tabs.constant";
import { ToolsConstants } from "@/constants/tools.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import { useGetParams } from "@/hooks/useGetParams";
import { PaginationMeta, usePagination } from "@/hooks/usePagination";
import {
  IProduct,
  IProductConsumed,
  IProductLogsItem,
} from "@/interfaces/tools.interface";
import {
  useGetConsumedProductsQuery,
  useGetProductsQuery,
} from "@/redux/api/toolsQuery";

const ProductPage = (): JSX.Element => {
  const [activeTab, setActiveTab] = React.useState(productTabs[0].value);
  const [isShowAddProduct, setIsShowAddProduct] = React.useState(false);
  const [isShowEditProduct, setIsShowEditProduct] = React.useState(false);
  const [onSiteSheet, setOnSiteSheet] = React.useState<{
    isOpen: boolean;
  } | null>(null);
  const [isShowProductDetailSheet, setIsShowProductDetailSheet] =
    React.useState(false);
  const [isShowAdjustQuantityModal, setIsShowAdjustQuantityModal] =
    React.useState(false);
  const [productLog, setProductLog] = React.useState<{
    isOpen: boolean;
    itemName: string;
    data: IProductLogsItem[];
  } | null>(null);

  const filters = useGetParams(baseFilterParams);

  const { data: productData, ...productRest } = useGetProductsQuery(
    {
      filters,
    },
    {
      skip: !filters.includes("limit=") || activeTab !== productTabs[0].value,
    },
  );

  const { data: consumedProductData, ...consumedRest } =
    useGetConsumedProductsQuery(
      {
        filters,
      },
      {
        skip: !filters.includes("limit=") || activeTab !== productTabs[1].value,
      },
    );

  const response = React.useMemo(() => {
    let res;
    let response;
    let pagination;

    switch (activeTab) {
      case productTabs[0].value:
        response = productData?.data;
        pagination = productData?.pagination;
        res = productRest;
        break;
      default:
        response = consumedProductData?.data;
        pagination = consumedProductData?.pagination;
        res = consumedRest;
        break;
    }

    return {
      response,
      pagination,
      rest: res,
    };
  }, [activeTab, consumedProductData, productData, productRest, consumedRest]);

  const pagination = usePagination(response?.pagination as PaginationMeta);

  const renderTableAccordingToTab = (): JSX.Element => {
    switch (activeTab) {
      case productTabs[0].value:
        return (
          <ProductTable
            data={(response?.response as IProduct[]) ?? []}
            toggleAddProductModal={() => setIsShowAddProduct(!isShowAddProduct)}
            toggleEditProductModal={() =>
              setIsShowEditProduct(!isShowEditProduct)
            }
            pagination={pagination}
            toggleVieLogSheet={setProductLog}
            toggleProductDetailSheet={() =>
              setIsShowProductDetailSheet(!isShowProductDetailSheet)
            }
            toggleAdjustQuantityModal={() =>
              setIsShowAdjustQuantityModal(!isShowAdjustQuantityModal)
            }
            toggleViewOnSiteSheet={setOnSiteSheet}
          />
        );
      case productTabs[1].value:
        return (
          <ConsumedTable
            data={(response?.response as IProductConsumed[]) ?? []}
            pagination={pagination}
            toggleViewLogSheet={setProductLog}
          />
        );
      default:
        return (
          <ProductTable
            data={(response?.response as IProduct[]) ?? []}
            toggleAddProductModal={() => setIsShowAddProduct(!isShowAddProduct)}
            toggleEditProductModal={() =>
              setIsShowEditProduct(!isShowEditProduct)
            }
            pagination={pagination}
            toggleVieLogSheet={setProductLog}
            toggleProductDetailSheet={() =>
              setIsShowProductDetailSheet(!isShowProductDetailSheet)
            }
            toggleAdjustQuantityModal={() =>
              setIsShowAdjustQuantityModal(!isShowAdjustQuantityModal)
            }
            toggleViewOnSiteSheet={setOnSiteSheet}
          />
        );
    }
  };

  return (
    <DataTableSlotContextProvider>
      <section className='space-y-3'>
        <CustomHeader
          title={ToolsConstants.INDEX_CONTENT_TEXT.PRODUCT_HEADER}
          tabs={productTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <FetchWrapper {...response.rest} isTable>
          {renderTableAccordingToTab()}
        </FetchWrapper>
      </section>
      {isShowAddProduct && (
        <AddProductModal
          isOpen={isShowAddProduct}
          toggleAddProductModal={() => setIsShowAddProduct(!isShowAddProduct)}
        />
      )}
      {isShowEditProduct && (
        <UpdateProductModal
          isOpen={isShowEditProduct}
          toggleUpdateProductModal={() =>
            setIsShowEditProduct(!isShowEditProduct)
          }
        />
      )}
      {productLog?.isOpen && (
        <ProductLog
          isOpen={productLog.isOpen}
          itemName={productLog.itemName}
          data={productLog.data}
          toggleProductLog={() => setProductLog(null)}
        />
      )}
      {isShowProductDetailSheet && (
        <ProductDetailSheet
          isOpen={isShowProductDetailSheet}
          toggleProductDetailSheet={() =>
            setIsShowProductDetailSheet(!isShowProductDetailSheet)
          }
        />
      )}
      {isShowAdjustQuantityModal && (
        <AdjustQuantityModal
          isOpen={isShowAdjustQuantityModal}
          toggleModal={() =>
            setIsShowAdjustQuantityModal(!isShowAdjustQuantityModal)
          }
        />
      )}
      {onSiteSheet?.isOpen && (
        <ProductOnsiteSheet
          isOpen={onSiteSheet?.isOpen}
          toggleOnSiteSheet={() => setOnSiteSheet(null)}
        />
      )}
    </DataTableSlotContextProvider>
  );
};

export default ProductPage;
